<template>
    <div class="keycare-statistics-page" v-loading="loading">
        <el-card shadow="never" class="filter-card">
            <div slot="header" class="card-header">
                <span>精准关怀体检统计分析</span>
                <el-button type="default" size="small" icon="el-icon-refresh" @click="getData">刷新</el-button>
            </div>
            <el-form :inline="true" :model="search" size="small">
                <el-form-item label="姓名">
                    <el-input v-model.trim="search.name" clearable style="width: 120px"></el-input>
                </el-form-item>
                <el-form-item label="身份证号">
                    <el-input v-model.trim="search.id_card" clearable style="width: 180px"></el-input>
                </el-form-item>
                <el-form-item label="二级单位">
                    <el-input v-model.trim="search.secondary_unit" clearable style="width: 180px"></el-input>
                </el-form-item>
                <el-form-item label="申请时间">
                    <el-date-picker
                        v-model="search.dateRange"
                        type="daterange"
                        range-separator="至"
                        start-placeholder="开始日期"
                        end-placeholder="结束日期"
                        value-format="yyyy-MM-dd"
                        style="width: 260px"
                    />
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" icon="el-icon-search" @click="getData">筛选</el-button>
                    <el-button @click="handleReset">重置</el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <el-row :gutter="12" class="section">
            <el-col :span="3" v-for="card in summaryCards" :key="card.key">
                <el-card shadow="hover" class="metric-card">
                    <div class="metric-title">{{ card.label }}</div>
                    <div class="metric-value" :class="card.className">{{ card.value }}</div>
                </el-card>
            </el-col>
        </el-row>

        <el-row :gutter="12" class="section">
            <el-col :span="12">
                <el-card shadow="never" class="panel-card">
                    <div slot="header" class="panel-header">流程转化与时效</div>
                    <div class="progress-item">
                        <div class="progress-label">审批通过率</div>
                        <el-progress :percentage="summary.approveRate" :stroke-width="14"></el-progress>
                    </div>
                    <div class="progress-item">
                        <div class="progress-label">报告上传率（通过后）</div>
                        <el-progress :percentage="summary.reportRate" status="success" :stroke-width="14"></el-progress>
                    </div>
                    <div class="progress-item">
                        <div class="progress-label">收费完成率（有报告）</div>
                        <el-progress :percentage="summary.payRate" status="warning" :stroke-width="14"></el-progress>
                    </div>
                    <el-divider></el-divider>
                    <el-row :gutter="12" class="timeliness-cards">
                        <el-col :span="8">
                            <div class="mini-stat">
                                <div class="mini-title">申请->审批</div>
                                <div class="mini-value">{{ formatHour(timeliness.applyToAuditAvgHour) }}</div>
                                <div class="mini-sub">48小时内：{{ timeliness.applyToAuditInSla }}%</div>
                            </div>
                        </el-col>
                        <el-col :span="8">
                            <div class="mini-stat">
                                <div class="mini-title">审批->报告</div>
                                <div class="mini-value">{{ formatHour(timeliness.auditToReportAvgHour) }}</div>
                                <div class="mini-sub">72小时内：{{ timeliness.auditToReportInSla }}%</div>
                            </div>
                        </el-col>
                        <el-col :span="8">
                            <div class="mini-stat">
                                <div class="mini-title">报告->收费</div>
                                <div class="mini-value">{{ formatHour(timeliness.reportToFeeAvgHour) }}</div>
                                <div class="mini-sub">72小时内：{{ timeliness.reportToFeeInSla }}%</div>
                            </div>
                        </el-col>
                    </el-row>
                </el-card>
            </el-col>

            <el-col :span="12">
                <el-card shadow="never" class="panel-card">
                    <div slot="header" class="panel-header">费用总览与分类占比</div>
                    <el-row :gutter="12" class="fee-head">
                        <el-col :span="8">
                            <div class="fee-box">
                                <div class="fee-label">总费用</div>
                                <div class="fee-value">{{ formatCurrency(summary.totalCost) }}</div>
                            </div>
                        </el-col>
                        <el-col :span="8">
                            <div class="fee-box">
                                <div class="fee-label">已收费</div>
                                <div class="fee-value paid">{{ formatCurrency(summary.paidCost) }}</div>
                            </div>
                        </el-col>
                        <el-col :span="8">
                            <div class="fee-box">
                                <div class="fee-label">待收费</div>
                                <div class="fee-value unpaid">{{ formatCurrency(summary.unpaidCost) }}</div>
                            </div>
                        </el-col>
                    </el-row>
                    <el-table :data="categoryCostRows" size="mini" border max-height="340">
                        <el-table-column prop="category" label="费用分类" min-width="120" />
                        <el-table-column prop="count" label="人次" width="80" align="center" />
                        <el-table-column label="费用" min-width="120" align="right">
                            <template slot-scope="scope">{{ formatCurrency(scope.row.cost) }}</template>
                        </el-table-column>
                        <el-table-column label="占比" width="90" align="center">
                            <template slot-scope="scope">{{ scope.row.ratio }}%</template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-col>
        </el-row>

        <el-row :gutter="12" class="section">
            <el-col :span="24">
                <el-card shadow="never" class="panel-card">
                    <div slot="header" class="panel-header">二级单位人数与费用统计</div>
                    <el-table :data="unitRows" size="mini" border max-height="420">
                        <el-table-column type="index" width="50" align="center" />
                        <el-table-column prop="secondary_unit" label="二级单位" min-width="180" />
                        <el-table-column prop="apply_count" label="申请人数" width="90" align="center" />
                        <el-table-column prop="approved_count" label="通过人数" width="90" align="center" />
                        <el-table-column prop="rejected_count" label="驳回人数" width="90" align="center" />
                        <el-table-column prop="report_count" label="报告上传" width="90" align="center" />
                        <el-table-column prop="paid_count" label="已收费" width="90" align="center" />
                        <el-table-column label="总费用" width="120" align="right">
                            <template slot-scope="scope">{{ formatCurrency(scope.row.total_cost) }}</template>
                        </el-table-column>
                        <el-table-column label="已收费金额" width="120" align="right">
                            <template slot-scope="scope">{{ formatCurrency(scope.row.paid_cost) }}</template>
                        </el-table-column>
                        <el-table-column label="人均费用" width="120" align="right">
                            <template slot-scope="scope">{{ formatCurrency(scope.row.avg_cost) }}</template>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-col>
        </el-row>
    </div>
</template>

<script>
const CATEGORY_RULES = [
    { category: '心血管风险', keywords: ['CTA', '冠脉', '动态心电', '头颈'] },
    { category: '脑血管风险', keywords: ['MRA', '颅脑'] },
    { category: '肺癌风险', keywords: ['肺小结节', '肺'] },
    { category: '乳腺癌风险', keywords: ['乳腺'] }
];

export default {
    name: 'keycare_statistics',
    path: '/main/keycare/statistics',
    meta: {
        title: '精准关怀体检统计',
    },
    data() {
        return {
            loading: false,
            search: {
                name: '',
                id_card: '',
                secondary_unit: '',
                dateRange: []
            },
            records: [],
            summary: {
                applyCount: 0,
                approvedCount: 0,
                rejectedCount: 0,
                reportCount: 0,
                paidCount: 0,
                approveRate: 0,
                reportRate: 0,
                payRate: 0,
                totalCost: 0,
                paidCost: 0,
                unpaidCost: 0
            },
            timeliness: {
                applyToAuditAvgHour: 0,
                auditToReportAvgHour: 0,
                reportToFeeAvgHour: 0,
                applyToAuditInSla: 0,
                auditToReportInSla: 0,
                reportToFeeInSla: 0
            },
            categoryCostRows: [],
            unitRows: []
        };
    },
    computed: {
        summaryCards() {
            return [
                { key: 'applyCount', label: '申请总数', value: this.summary.applyCount, className: '' },
                { key: 'approvedCount', label: '审批通过', value: this.summary.approvedCount, className: 'ok' },
                { key: 'rejectedCount', label: '审批不通过', value: this.summary.rejectedCount, className: 'bad' },
                { key: 'reportCount', label: '已上传报告', value: this.summary.reportCount, className: '' },
                { key: 'paidCount', label: '已收费', value: this.summary.paidCount, className: '' },
                { key: 'pendingAudit', label: '待审核', value: this.summary.applyCount - this.summary.approvedCount - this.summary.rejectedCount, className: '' },
                { key: 'pendingReport', label: '待上传报告', value: Math.max(this.summary.approvedCount - this.summary.reportCount, 0), className: '' },
                { key: 'pendingPay', label: '待收费', value: Math.max(this.summary.reportCount - this.summary.paidCount, 0), className: '' }
            ];
        }
    },
    methods: {
        formatCurrency(num) {
            const n = Number(num || 0);
            return `${n.toFixed(2)} 元`;
        },
        formatHour(num) {
            const n = Number(num || 0);
            if (!n) return '-';
            return `${n.toFixed(1)} 小时`;
        },
        toPercent(value, total) {
            if (!total) return 0;
            return Number(((value / total) * 100).toFixed(1));
        },
        parseDate(value) {
            if (!value) return null;
            const d = new Date(value);
            return Number.isNaN(d.getTime()) ? null : d;
        },
        hoursBetween(start, end) {
            if (!start || !end) return null;
            const s = this.parseDate(start);
            const e = this.parseDate(end);
            if (!s || !e) return null;
            return (e.getTime() - s.getTime()) / 3600000;
        },
        inferCategory(itemName) {
            const name = String(itemName || '');
            const found = CATEGORY_RULES.find(rule => rule.keywords.some(k => name.includes(k)));
            return found ? found.category : '其他';
        },
        hasReport(row) {
            return !!(
                row.report_url ||
                row.report_path ||
                row.report_name ||
                row.report_file ||
                row.report_id ||
                row.report_uploaded ||
                row.report_time ||
                row.report_upload_time ||
                row.report_uploaded_at
            );
        },
        isPaid(row) {
            return Number(row.status) === 2 || !!(row.fee_time || row.charge_time || row.paid_time);
        },
        getApplyTime(row) {
            return row.created || row.create_time;
        },
        getAuditTime(row) {
            return row.audit_time;
        },
        getReportTime(row) {
            return row.report_time || row.report_upload_time || row.report_uploaded_at;
        },
        getFeeTime(row) {
            return row.fee_time || row.charge_time || row.paid_time;
        },
        handleReset() {
            this.search = {
                name: '',
                id_card: '',
                secondary_unit: '',
                dateRange: []
            };
            this.getData();
        },
        async getData() {
            this.loading = true;
            try {
                const params = {
                    page: 1,
                    pageSize: 5000,
                    name: this.search.name,
                    id_card: this.search.id_card,
                    secondary_unit: this.search.secondary_unit
                };
                if (this.search.dateRange && this.search.dateRange.length === 2) {
                    params.start_date = this.search.dateRange[0];
                    params.end_date = this.search.dateRange[1];
                }
                const resp = await this.$http.get('/keycare/register', { params });
                const payload = resp.data || {};
                const rows = payload.data && payload.data.records
                    ? payload.data.records
                    : (Array.isArray(payload.data) ? payload.data : []);
                this.records = rows;
                this.buildStats(rows);
            } catch (e) {
                this.$message.error(e.message || '获取统计数据失败');
                this.records = [];
                this.buildStats([]);
            }
            this.loading = false;
        },
        buildStats(rows) {
            const applyCount = rows.length;
            const approvedRows = rows.filter(row => Number(row.status) === 1 || Number(row.status) === 2);
            const rejectedRows = rows.filter(row => Number(row.status) === -1);
            const reportRows = rows.filter(row => this.hasReport(row));
            const paidRows = rows.filter(row => this.isPaid(row));

            const totalCost = rows.reduce((sum, row) => sum + Number(row.total_cost || 0), 0);
            const paidCost = paidRows.reduce((sum, row) => sum + Number(row.total_cost || 0), 0);

            this.summary = {
                applyCount,
                approvedCount: approvedRows.length,
                rejectedCount: rejectedRows.length,
                reportCount: reportRows.length,
                paidCount: paidRows.length,
                approveRate: this.toPercent(approvedRows.length, applyCount),
                reportRate: this.toPercent(reportRows.length, approvedRows.length || 1),
                payRate: this.toPercent(paidRows.length, reportRows.length || 1),
                totalCost,
                paidCost,
                unpaidCost: totalCost - paidCost
            };

            this.buildTimeliness(rows);
            this.buildCategoryCost(rows);
            this.buildUnitStats(rows);
        },
        buildTimeliness(rows) {
            const a2a = [];
            const a2r = [];
            const r2f = [];
            let a2aInSla = 0;
            let a2rInSla = 0;
            let r2fInSla = 0;

            rows.forEach(row => {
                const applyTime = this.getApplyTime(row);
                const auditTime = this.getAuditTime(row);
                const reportTime = this.getReportTime(row);
                const feeTime = this.getFeeTime(row);

                const h1 = this.hoursBetween(applyTime, auditTime);
                if (h1 !== null && h1 >= 0) {
                    a2a.push(h1);
                    if (h1 <= 48) a2aInSla += 1;
                }

                const h2 = this.hoursBetween(auditTime, reportTime);
                if (h2 !== null && h2 >= 0) {
                    a2r.push(h2);
                    if (h2 <= 72) a2rInSla += 1;
                }

                const h3 = this.hoursBetween(reportTime, feeTime);
                if (h3 !== null && h3 >= 0) {
                    r2f.push(h3);
                    if (h3 <= 72) r2fInSla += 1;
                }
            });

            const avg = (arr) => (arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0);
            this.timeliness = {
                applyToAuditAvgHour: avg(a2a),
                auditToReportAvgHour: avg(a2r),
                reportToFeeAvgHour: avg(r2f),
                applyToAuditInSla: this.toPercent(a2aInSla, a2a.length),
                auditToReportInSla: this.toPercent(a2rInSla, a2r.length),
                reportToFeeInSla: this.toPercent(r2fInSla, r2f.length)
            };
        },
        buildCategoryCost(rows) {
            const costMap = {};
            const countMap = {};

            rows.forEach(row => {
                const items = Array.isArray(row.items) ? row.items : [];
                const totalCost = Number(row.total_cost || 0);
                if (!items.length) {
                    const cat = '其他';
                    costMap[cat] = (costMap[cat] || 0) + totalCost;
                    countMap[cat] = (countMap[cat] || 0) + 1;
                    return;
                }
                const perItemCost = totalCost / items.length;
                items.forEach(item => {
                    const cat = this.inferCategory(item);
                    costMap[cat] = (costMap[cat] || 0) + perItemCost;
                    countMap[cat] = (countMap[cat] || 0) + 1;
                });
            });

            const total = Object.values(costMap).reduce((sum, v) => sum + v, 0);
            this.categoryCostRows = Object.keys(costMap)
                .map(cat => ({
                    category: cat,
                    count: countMap[cat] || 0,
                    cost: Number(costMap[cat].toFixed(2)),
                    ratio: this.toPercent(costMap[cat], total)
                }))
                .sort((a, b) => b.cost - a.cost);
        },
        buildUnitStats(rows) {
            const map = {};
            rows.forEach(row => {
                const key = row.secondary_unit || '未分配单位';
                if (!map[key]) {
                    map[key] = {
                        secondary_unit: key,
                        apply_count: 0,
                        approved_count: 0,
                        rejected_count: 0,
                        report_count: 0,
                        paid_count: 0,
                        total_cost: 0,
                        paid_cost: 0,
                        avg_cost: 0
                    };
                }
                const unit = map[key];
                unit.apply_count += 1;
                if (Number(row.status) === 1 || Number(row.status) === 2) unit.approved_count += 1;
                if (Number(row.status) === -1) unit.rejected_count += 1;
                if (this.hasReport(row)) unit.report_count += 1;
                if (this.isPaid(row)) unit.paid_count += 1;
                const cost = Number(row.total_cost || 0);
                unit.total_cost += cost;
                if (this.isPaid(row)) unit.paid_cost += cost;
            });

            this.unitRows = Object.values(map)
                .map(row => Object.assign({}, row, {
                    avg_cost: row.apply_count ? Number((row.total_cost / row.apply_count).toFixed(2)) : 0,
                    total_cost: Number(row.total_cost.toFixed(2)),
                    paid_cost: Number(row.paid_cost.toFixed(2))
                }))
                .sort((a, b) => b.total_cost - a.total_cost);
        }
    },
    mounted() {
        this.getData();
    }
};
</script>

<style lang="scss" scoped>
.keycare-statistics-page {
    padding: 0px;
}

.card-header,
.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
    color: #303133;
}

.section {
    margin-top: 12px;
}

.metric-card {
    text-align: center;
}

.metric-title {
    color: #909399;
    font-size: 12px;
}

.metric-value {
    margin-top: 8px;
    font-size: 24px;
    font-weight: 700;
    color: #303133;
}

.metric-value.ok {
    color: #67c23a;
}

.metric-value.bad {
    color: #f56c6c;
}

.panel-card {
    min-height: 420px;
}

.progress-item {
    margin-bottom: 12px;
}

.progress-label {
    margin-bottom: 6px;
    color: #606266;
    font-size: 13px;
}

.timeliness-cards {
    margin-top: 4px;
}

.mini-stat {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 10px;
    background: #fafbfc;
}

.mini-title {
    font-size: 12px;
    color: #909399;
}

.mini-value {
    margin-top: 6px;
    font-size: 20px;
    color: #303133;
    font-weight: 700;
}

.mini-sub {
    margin-top: 4px;
    font-size: 12px;
    color: #606266;
}

.fee-head {
    margin-bottom: 10px;
}

.fee-box {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 10px;
    background: #fafbfc;
}

.fee-label {
    font-size: 12px;
    color: #909399;
}

.fee-value {
    margin-top: 6px;
    font-size: 18px;
    font-weight: 700;
    color: #303133;
}

.fee-value.paid {
    color: #67c23a;
}

.fee-value.unpaid {
    color: #e6a23c;
}
</style>
